import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, vModelDynamic as _vModelDynamic, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, createVNode as _createVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "min-h-[68vh] w-full" }
const _hoisted_2 = { class: "mx-auto mt-14 w-full max-w-xl px-4" }
const _hoisted_3 = {
  key: 0,
  class: "rounded-2xl bg-white p-6 shadow-[0_8px_24px_rgba(16,24,40,.08)] ring-1 ring-slate-100 md:p-8"
}
const _hoisted_4 = { class: "relative" }
const _hoisted_5 = ["type"]
const _hoisted_6 = {
  key: 0,
  xmlns: "http://www.w3.org/2000/svg",
  class: "h-5 w-5",
  fill: "none",
  viewBox: "0 0 24 24",
  stroke: "currentColor"
}
const _hoisted_7 = {
  key: 1,
  xmlns: "http://www.w3.org/2000/svg",
  class: "h-5 w-5",
  viewBox: "0 0 24 24",
  fill: "currentColor"
}
const _hoisted_8 = { class: "mt-2 flex items-center gap-3" }
const _hoisted_9 = { class: "h-1.5 w-full overflow-hidden rounded-full bg-slate-100" }
const _hoisted_10 = { class: "mt-6" }
const _hoisted_11 = { class: "relative" }
const _hoisted_12 = ["type"]
const _hoisted_13 = {
  key: 0,
  xmlns: "http://www.w3.org/2000/svg",
  class: "h-5 w-5",
  fill: "none",
  viewBox: "0 0 24 24",
  stroke: "currentColor"
}
const _hoisted_14 = {
  key: 1,
  xmlns: "http://www.w3.org/2000/svg",
  class: "h-5 w-5",
  viewBox: "0 0 24 24",
  fill: "currentColor"
}
const _hoisted_15 = {
  key: 0,
  class: "mt-2 text-[12px] font-medium text-rose-600"
}
const _hoisted_16 = {
  key: 1,
  class: "mt-1 text-[12px] font-medium text-rose-600"
}
const _hoisted_17 = { class: "mt-7" }
const _hoisted_18 = ["disabled"]
const _hoisted_19 = {
  key: 0,
  class: "-ml-1 mr-2 h-4 w-4 animate-spin",
  viewBox: "0 0 24 24"
}
const _hoisted_20 = {
  key: 1,
  class: "rounded-2xl border border-rose-100 bg-rose-50 p-6 text-rose-800 shadow-sm md:p-8"
}
const _hoisted_21 = {
  key: 2,
  class: "flex items-center justify-center gap-3 py-24 text-slate-600"
}

import { computed, onMounted, ref, watch } from 'vue'
import Auth from '@/composables/Auth'
import { handleApiError } from '@/utils/common'
import type { NotificationType } from '@/types'
import Notification from '@/widgets/Notification.vue'
import router from '@/router'


export default /*@__PURE__*/_defineComponent({
  __name: 'ResetPassword',
  props: {
    token: {}
  },
  setup(__props: any) {

const props = __props

// state
const password = ref<string>('')
const passwordConfirmationn = ref<string>('')

const showNew = ref(false)
const showRepeat = ref(false)

const isLoading = ref(false)
const status = ref<boolean | string>(false) // true | 'not-found' | false
const notif = ref<NotificationType>({ show: false, type: 'success', message: '', messageDetails: ''})

const passwordError = ref<string>('')       // inline error
const confirmError  = ref<string>('')       // inline error

const { resetPassword, checkResetToken }  = Auth()

// strength meter (0..100)
const strength = ref(0)
watch(password, (v) => {
  let score = 0
  if (v.length >= 6) score += 25
  if (/[A-Z]/.test(v)) score += 20
  if (/[a-z]/.test(v)) score += 15
  if (/\d/.test(v)) score += 20
  if (/[^A-Za-z0-9]/.test(v)) score += 20
  strength.value = Math.min(100, score)
})

const strengthLabel = computed(() => {
  if (strength.value < 30) return 'Weak'
  if (strength.value < 70) return 'Medium'
  return 'Strong'
})
const strengthBarClass = computed(() => {
  if (strength.value < 30) return 'bg-orange-400'
  if (strength.value < 70) return 'bg-amber-500'
  return 'bg-emerald-500'
})
const strengthTextClass = computed(() => {
  if (strength.value < 30) return 'text-orange-500'
  if (strength.value < 70) return 'text-amber-600'
  return 'text-emerald-600'
})

const canSubmit = computed(() =>
  password.value.length >= 6 &&
  passwordConfirmationn.value.length >= 6 &&
  password.value === passwordConfirmationn.value
)

const validate = () => {
  passwordError.value = ''
  confirmError.value = ''

  if (!password.value) {
    passwordError.value = 'Password is required'
  } else if (password.value.length < 6) {
    passwordError.value = 'Must be at least 6 characters long'
  }

  if (!passwordConfirmationn.value) {
    confirmError.value = 'Please re-enter the password'
  } else if (password.value && password.value !== passwordConfirmationn.value) {
    confirmError.value = 'Passwords do not match'
  }

  return !(passwordError.value || confirmError.value)
}

const handleResetPassword = async () => {
  if (!validate()) return
  isLoading.value = true
  const response = await resetPassword(password.value, passwordConfirmationn.value, props.token)
  isLoading.value = false

  if (response.status === 200) {
    password.value = ''
    passwordConfirmationn.value = ''
    router.push({ name: 'Login' })
  } else {
    const error = handleApiError(response)
    notif.value = { show: true, type: 'error', message: error, messageDetails: '' }
    setTimeout(() => notifClosed(), 4000)
  }
}

const notifClosed = () => { notif.value.show = false }

onMounted(async () => {
  if (!props.token) {
    status.value = 'not-found'
    return
  }
  const res = await checkResetToken(props.token)
  status.value = res.status === 200 ? true : 'not-found'
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        (status.value === true)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _cache[11] || (_cache[11] = _createElementVNode("h1", { class: "mb-2 text-2xl font-semibold text-slate-900" }, "Reset password", -1)),
              _cache[12] || (_cache[12] = _createElementVNode("p", { class: "mb-6 text-[14px] text-slate-500" }, " Choose a new password for your account. ", -1)),
              _cache[13] || (_cache[13] = _createElementVNode("label", { class: "mb-1 block text-[12px] font-medium text-slate-600 tracking-wide" }, [
                _createTextVNode(" NEW PASSWORD "),
                _createElementVNode("span", { class: "text-rose-500" }, "*")
              ], -1)),
              _createElementVNode("div", _hoisted_4, [
                _withDirectives(_createElementVNode("input", {
                  type: showNew.value ? 'text' : 'password',
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((password).value = $event)),
                  autocomplete: "new-password",
                  class: "block w-full rounded-lg border border-slate-200 px-4 py-3 text-[15px] font-semibold text-slate-800 placeholder-slate-400 outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-50",
                  placeholder: "Enter a strong password"
                }, null, 8, _hoisted_5), [
                  [
                    _vModelDynamic,
                    password.value,
                    void 0,
                    { trim: true }
                  ]
                ]),
                _createElementVNode("button", {
                  type: "button",
                  class: "absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2 text-slate-500 hover:bg-slate-50",
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (showNew.value = !showNew.value)),
                  "aria-label": "Toggle password visibility"
                }, [
                  (!showNew.value)
                    ? (_openBlock(), _createElementBlock("svg", _hoisted_6, _cache[4] || (_cache[4] = [
                        _createElementVNode("path", {
                          "stroke-linecap": "round",
                          "stroke-linejoin": "round",
                          "stroke-width": "1.5",
                          d: "M2.036 12.322a1.012 1.012 0 010-.644C3.423 7.51 7.36 5 12 5c4.64 0 8.577 2.51 9.964 6.678.07.214.07.45 0 .644C20.577 16.49 16.64 19 12 19c-4.64 0-8.577-2.51-9.964-6.678z"
                        }, null, -1),
                        _createElementVNode("path", {
                          "stroke-linecap": "round",
                          "stroke-linejoin": "round",
                          "stroke-width": "1.5",
                          d: "M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        }, null, -1)
                      ])))
                    : (_openBlock(), _createElementBlock("svg", _hoisted_7, _cache[5] || (_cache[5] = [
                        _createElementVNode("path", {
                          "fill-rule": "evenodd",
                          d: "M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-2.29-2.29c1.01-.86 1.86-1.95 2.46-3.16.15-.31.15-.67 0-.98C20.58 8.51 16.64 6 12 6c-1.22 0-2.4.19-3.5.54L3.53 2.47Zm10.94 12.06-5.01-5.01A3 3 0 0 0 12 15a3 3 0 0 0 2.47-1.47ZM5.32 7.15 6.8 8.63A12.3 12.3 0 0 0 3.54 12c1.39 3.17 5.33 5.68 8.46 5.68 1.14 0 2.26-.2 3.3-.56l1.49 1.49A12.83 12.83 0 0 1 12 19C7.36 19 3.42 16.49 2.04 12.32a1.6 1.6 0 0 1 0-1.28c.72-2.03 2.14-3.74 3.28-4.89Z",
                          "clip-rule": "evenodd"
                        }, null, -1)
                      ])))
                ])
              ]),
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("div", {
                    class: _normalizeClass(["h-full", strengthBarClass.value]),
                    style: _normalizeStyle({ width: strength.value + '%' })
                  }, null, 6)
                ]),
                _createElementVNode("span", {
                  class: _normalizeClass(["min-w-[52px] text-[12px] font-medium", strengthTextClass.value])
                }, _toDisplayString(strengthLabel.value), 3)
              ]),
              _createElementVNode("div", _hoisted_10, [
                _cache[8] || (_cache[8] = _createElementVNode("label", { class: "mb-1 block text-[12px] font-medium text-slate-600 tracking-wide" }, [
                  _createTextVNode(" REPEAT PASSWORD "),
                  _createElementVNode("span", { class: "text-rose-500" }, "*")
                ], -1)),
                _createElementVNode("div", _hoisted_11, [
                  _withDirectives(_createElementVNode("input", {
                    type: showRepeat.value ? 'text' : 'password',
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((passwordConfirmationn).value = $event)),
                    autocomplete: "new-password",
                    class: "block w-full rounded-lg border border-slate-200 px-4 py-3 text-[15px] font-semibold text-slate-800 placeholder-slate-400 outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-50",
                    placeholder: "Re-enter your password"
                  }, null, 8, _hoisted_12), [
                    [
                      _vModelDynamic,
                      passwordConfirmationn.value,
                      void 0,
                      { trim: true }
                    ]
                  ]),
                  _createElementVNode("button", {
                    type: "button",
                    class: "absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2 text-slate-500 hover:bg-slate-50",
                    onClick: _cache[3] || (_cache[3] = ($event: any) => (showRepeat.value = !showRepeat.value)),
                    "aria-label": "Toggle password visibility"
                  }, [
                    (!showRepeat.value)
                      ? (_openBlock(), _createElementBlock("svg", _hoisted_13, _cache[6] || (_cache[6] = [
                          _createElementVNode("path", {
                            "stroke-linecap": "round",
                            "stroke-linejoin": "round",
                            "stroke-width": "1.5",
                            d: "M2.036 12.322a1.012 1.012 0 010-.644C3.423 7.51 7.36 5 12 5c4.64 0 8.577 2.51 9.964 6.678.07.214.07.45 0 .644C20.577 16.49 16.64 19 12 19c-4.64 0-8.577-2.51-9.964-6.678z"
                          }, null, -1),
                          _createElementVNode("path", {
                            "stroke-linecap": "round",
                            "stroke-linejoin": "round",
                            "stroke-width": "1.5",
                            d: "M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          }, null, -1)
                        ])))
                      : (_openBlock(), _createElementBlock("svg", _hoisted_14, _cache[7] || (_cache[7] = [
                          _createElementVNode("path", {
                            "fill-rule": "evenodd",
                            d: "M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-2.29-2.29c1.01-.86 1.86-1.95 2.46-3.16.15-.31.15-.67 0-.98C20.58 8.51 16.64 6 12 6c-1.22 0-2.4.19-3.5.54L3.53 2.47Zm10.94 12.06-5.01-5.01A3 3 0 0 0 12 15a3 3 0 0 0 2.47-1.47ZM5.32 7.15 6.8 8.63A12.3 12.3 0 0 0 3.54 12c1.39 3.17 5.33 5.68 8.46 5.68 1.14 0 2.26-.2 3.3-.56l1.49 1.49A12.83 12.83 0 0 1 12 19C7.36 19 3.42 16.49 2.04 12.32a1.6 1.6 0 0 1 0-1.28c.72-2.03 2.14-3.74 3.28-4.89Z",
                            "clip-rule": "evenodd"
                          }, null, -1)
                        ])))
                  ])
                ]),
                (passwordError.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_15, _toDisplayString(passwordError.value), 1))
                  : _createCommentVNode("", true),
                (confirmError.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_16, _toDisplayString(confirmError.value), 1))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_17, [
                _createElementVNode("button", {
                  disabled: isLoading.value || !canSubmit.value,
                  onClick: handleResetPassword,
                  class: "inline-flex w-full items-center justify-center rounded-lg bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                }, [
                  (isLoading.value)
                    ? (_openBlock(), _createElementBlock("svg", _hoisted_19, _cache[9] || (_cache[9] = [
                        _createElementVNode("circle", {
                          cx: "12",
                          cy: "12",
                          r: "10",
                          stroke: "currentColor",
                          "stroke-width": "4",
                          class: "opacity-20"
                        }, null, -1),
                        _createElementVNode("path", {
                          d: "M4 12a8 8 0 018-8",
                          stroke: "currentColor",
                          "stroke-width": "4",
                          class: "opacity-90"
                        }, null, -1)
                      ])))
                    : _createCommentVNode("", true),
                  _cache[10] || (_cache[10] = _createTextVNode(" UPDATE PASSWORD "))
                ], 8, _hoisted_18)
              ])
            ]))
          : (status.value === 'not-found')
            ? (_openBlock(), _createElementBlock("div", _hoisted_20, _cache[14] || (_cache[14] = [
                _createElementVNode("div", { class: "flex items-start gap-3" }, [
                  _createElementVNode("div", { class: "mt-0.5 rounded-full bg-white p-2 text-rose-500 ring-1 ring-rose-200" }, " ⚠️ "),
                  _createElementVNode("div", null, [
                    _createElementVNode("h3", { class: "text-[16px] font-semibold" }, "Invalid token"),
                    _createElementVNode("p", { class: "mt-1 text-[14px]" }, "The reset link is invalid or has expired.")
                  ])
                ], -1)
              ])))
            : (_openBlock(), _createElementBlock("div", _hoisted_21, _cache[15] || (_cache[15] = [
                _createElementVNode("img", {
                  class: "h-6 w-6 animate-spin",
                  src: "/assets/loading.svg",
                  alt: ""
                }, null, -1),
                _createElementVNode("span", { class: "text-[14px]" }, "Loading… please wait", -1)
              ])))
      ])
    ]),
    _createVNode(Notification, {
      show: notif.value.show,
      type: notif.value.type,
      message: notif.value.message,
      messageDetails: notif.value.messageDetails,
      onClosed: notifClosed
    }, null, 8, ["show", "type", "message", "messageDetails"])
  ], 64))
}
}

})